import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Settings, Map, Plane, Shield, Volume2, ChevronUp, Globe } from "lucide-react";
import { useStore } from "@/store/store";
import { useCallback } from "react";
import { MapStyleSelector } from "./style-selector";
import { ProjectionSelector } from "./projection-selector";
import { initAudioContext } from "@/lib/audio";
import { useTheme } from "@/components/theme-provider";

export const MapSettings: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { theme } = useTheme();

  // Map layer toggles
  const shouldShowAircraft = useStore(
    useCallback((state) => state.shouldShowAircraft, [])
  );
  const setShouldShowAircraft = useStore(
    useCallback((state) => state.setShouldShowAircraft, [])
  );
  const shouldShowFlightRestrictions = useStore(
    useCallback((state) => state.shouldShowFlightRestrictions, [])
  );
  const setShouldShowFlightRestrictions = useStore(
    useCallback((state) => state.setShouldShowFlightRestrictions, [])
  );

  // Audio alerts
  const alertsEnabled = useStore(
    useCallback((state) => state.alertsEnabled, [])
  );
  const toggleAlerts = useStore(useCallback((state) => state.toggleAlerts, []));

  const handleToggleAlerts = () => {
    if (!alertsEnabled) {
      initAudioContext();
    }
    toggleAlerts();
  };

  return (
    <Card
      className={`border transition-all duration-200 ${
        isExpanded ? "w-80" : "w-12 p-0 py-2"
      } ${
        theme === "dark"
          ? "bg-gray-900/95 text-white border-gray-700"
          : "bg-white/95 text-black border-gray-200"
      }`}
    >
      {!isExpanded ? (
        <div
          className="flex items-center justify-center p-0 cursor-pointer hover:bg-gray-800/20 dark:hover:bg-gray-100/20 transition-colors relative group"
          onClick={() => setIsExpanded(true)}
          title="Map Settings"
        >
          <Settings className="h-5 w-5" />
        </div>
      ) : (
        <>
          <div
            className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-800/20 dark:hover:bg-gray-100/20 transition-colors"
            onClick={() => setIsExpanded(false)}
          >
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              <h3 className="font-medium text-sm">Map Settings</h3>
            </div>
            <ChevronUp className="h-4 w-4" />
          </div>

          <div className="px-3 pb-3 space-y-4">
            {/* Map Style */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Map className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Map Style</Label>
              </div>
              <MapStyleSelector />
            </div>

            {/* Projection */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Projection</Label>
              </div>
              <ProjectionSelector />
            </div>
            {/* Layer Toggles */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Plane className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm">Aircraft</Label>
                </div>
                <Switch
                  checked={shouldShowAircraft}
                  onCheckedChange={() =>
                    setShouldShowAircraft(!shouldShowAircraft)
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm">Airspace Zones</Label>
                </div>
                <Switch
                  checked={shouldShowFlightRestrictions}
                  onCheckedChange={() =>
                    setShouldShowFlightRestrictions(
                      !shouldShowFlightRestrictions
                    )
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Volume2 className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm">Audio Alerts</Label>
                </div>
                <Switch
                  checked={alertsEnabled}
                  onCheckedChange={handleToggleAlerts}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};
